import { monthKeyOfDay } from './dates'
import { isInRoster, rosterFor } from './roster'
import type { AttendanceRecord, AttendanceStatus, DayKey, Meeting, Member, MonthKey } from './types'

/** Attendance lookups and counts. Build the index once, then look up — never scan per row. */

export type AttendanceIndex = ReadonlyMap<string, AttendanceRecord>

export const attendanceKey = (meetingId: string, memberId: string) => `${meetingId}|${memberId}`

export function indexAttendance(
  records: Iterable<AttendanceRecord>,
): Map<string, AttendanceRecord> {
  const index = new Map<string, AttendanceRecord>()
  for (const r of records) index.set(attendanceKey(r.meetingId, r.memberId), r)
  return index
}

export function recordOf(
  index: AttendanceIndex,
  meetingId: string,
  memberId: string,
): AttendanceRecord | undefined {
  return index.get(attendanceKey(meetingId, memberId))
}

export function statusOf(
  index: AttendanceIndex,
  meetingId: string,
  memberId: string,
): AttendanceStatus {
  return recordOf(index, meetingId, memberId)?.status ?? 'unmarked'
}

/**
 * What one tap on a name does. Present toggles back to unmarked (the tap is
 * its own undo); an absence becomes present, because the common correction is
 * "they turned up after all".
 */
export function nextOnTap(current: AttendanceStatus): AttendanceStatus {
  return current === 'present' ? 'unmarked' : 'present'
}

export const isAbsence = (s: AttendanceStatus) => s === 'excused' || s === 'absent'

export interface StatusCounts {
  present: number
  excused: number
  absent: number
  unmarked: number
  /** Everyone counted: the four above summed. */
  total: number
}

export const emptyCounts = (): StatusCounts => ({
  present: 0,
  excused: 0,
  absent: 0,
  unmarked: 0,
  total: 0,
})

function add(counts: StatusCounts, status: AttendanceStatus): void {
  counts[status] += 1
  counts.total += 1
}

/** Counts over the roster of one meeting. */
export function countMeeting(
  meeting: Pick<Meeting, 'id' | 'date'>,
  members: readonly Member[],
  index: AttendanceIndex,
): StatusCounts {
  const counts = emptyCounts()
  for (const m of members) {
    if (isInRoster(m, meeting.date)) add(counts, statusOf(index, meeting.id, m.id))
  }
  return counts
}

/** Counts for one member over the meetings whose roster includes them. */
export function countMember(
  member: Member,
  meetings: readonly Meeting[],
  index: AttendanceIndex,
): StatusCounts {
  const counts = emptyCounts()
  for (const mt of meetings) {
    if (!mt.deleted && isInRoster(member, mt.date)) add(counts, statusOf(index, mt.id, member.id))
  }
  return counts
}

/** Roster members nobody has marked yet — the target of "everyone else is here". */
export function unmarkedMembers(
  meeting: Pick<Meeting, 'id' | 'date'>,
  members: readonly Member[],
  index: AttendanceIndex,
): Member[] {
  return rosterFor(members, meeting.date).filter(
    (m) => statusOf(index, meeting.id, m.id) === 'unmarked',
  )
}

export function compareMeetings(a: Meeting, b: Meeting): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1
  const ta = a.time ?? ''
  const tb = b.time ?? ''
  if (ta !== tb) return ta < tb ? -1 : 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** Live meetings of a month, in date order. */
export function meetingsInMonth(meetings: readonly Meeting[], month: MonthKey): Meeting[] {
  return meetings.filter((m) => !m.deleted && monthKeyOfDay(m.date) === month).sort(compareMeetings)
}

/** Live meetings held on one day, in time order. */
export function meetingsOn(meetings: readonly Meeting[], day: DayKey): Meeting[] {
  return meetings.filter((m) => !m.deleted && m.date === day).sort(compareMeetings)
}
